import React, { useState } from "react";

export default function Resources() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const glossary = [
    {
      term: "Osrey",
      definition: "The core concept and brand representing a modern, curated approach to connected information and community-driven content."
    },
    {
      term: "Osreys",
      definition: "Plural form referring to multiple instances, variations, or aspects of the Osrey ecosystem and methodology."
    },
    {
      term: "Osrey Hub",
      definition: "Central knowledge repository containing all resources, guides, and documentation about Osrey."
    },
    {
      term: "Osrey Community",
      definition: "The collective group of users, contributors, and enthusiasts who engage with and shape the Osrey ecosystem."
    },
    {
      term: "Osrey Framework",
      definition: "The underlying structure and principles that guide the implementation and evolution of Osrey concepts."
    }
  ];

  const faqs = [
    {
      question: "What exactly is Osrey?",
      answer: "Osrey is a modern, community-driven platform focused on curating and connecting information in a premium, accessible way. It represents a new approach to organizing knowledge and building engaged communities around shared interests."
    },
    {
      question: "How do Osreys differ from Osrey?",
      answer: "While Osrey refers to the singular concept and brand, Osreys represents the plural form—multiple implementations, variations, or instances within the Osrey ecosystem. Think of Osrey as the philosophy and Osreys as the practical applications."
    },
    {
      question: "Is Osrey free to use?",
      answer: "Yes, Osrey provides free access to all core resources, articles, and community features. Premium features and advanced tools may be available through subscription plans in the future."
    },
    {
      question: "How can I contribute to the Osrey community?",
      answer: "You can contribute by sharing insights, participating in discussions, creating content, and helping other community members. Visit our Contact page to learn more about contribution opportunities."
    },
    {
      question: "Where can I learn more about implementing Osrey concepts?",
      answer: "Our comprehensive guides and articles in the Blog section cover everything from basic concepts to advanced implementations. Start with 'Osrey — The Complete Guide' for a thorough introduction."
    },
    {
      question: "What makes Osrey different from other platforms?",
      answer: "Osrey focuses on premium quality, community-driven content, and a modern approach to information curation. We prioritize clarity, accessibility, and creating genuine value for our users rather than chasing trends."
    }
  ];

  const downloads = [
    {
      title: "Osrey Quick Start Guide",
      description: "A comprehensive PDF guide to getting started with Osrey concepts and implementations.",
      type: "PDF",
      size: "2.4 MB"
    },
    {
      title: "Osreys Implementation Checklist",
      description: "Step-by-step checklist for implementing Osrey principles in your projects.",
      type: "PDF",
      size: "1.1 MB"
    },
    {
      title: "Osrey Community Guidelines",
      description: "Best practices and guidelines for participating in the Osrey community.",
      type: "PDF",
      size: "856 KB"
    },
    {
      title: "Osrey Brand Assets",
      description: "Official logos, colors, and brand guidelines for Osrey.",
      type: "ZIP",
      size: "5.2 MB"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0D12] text-white">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-[#0A0D12] via-[#1C2A35] to-[#0A0D12]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-[#00D4AA] to-[#FF6B35] bg-clip-text text-transparent">
            Osrey Resources Hub
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Your comprehensive knowledge center for everything Osrey and Osreys. 
            Explore our glossary, download guides, and find answers to common questions.
          </p>
        </div>
      </section>

      {/* Glossary Section */}
      <section className="py-16 px-6 bg-[#0A0D12]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold mb-4 text-center">Osrey Glossary</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Essential terminology for understanding Osrey and Osreys concepts
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {glossary.map((item, index) => (
              <div
                key={index}
                className="bg-[#1C2A35] p-6 rounded-lg border border-[#3A4E5F] hover:border-[#00D4AA] transition-colors"
              >
                <h3 className="text-2xl font-bold text-[#00D4AA] mb-3">
                  {item.term}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {item.definition}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Downloads Section */}
      <section className="py-16 px-6 bg-[#0A0D12]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold mb-4 text-center">Downloadable Guides</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Free resources to help you master Osrey and implement Osreys effectively
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {downloads.map((item, index) => (
              <div
                key={index}
                className="bg-[#1C2A35] p-6 rounded-lg border border-[#3A4E5F] hover:border-[#00D4AA] transition-all duration-300 group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#00D4AA] transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-3">
                      {item.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="px-3 py-1 bg-[#3A4E5F] rounded-full">
                        {item.type}
                      </span>
                      <span>{item.size}</span>
                    </div>
                  </div>
                  <button className="ml-4 p-3 bg-[#00D4AA] hover:bg-[#00B890] text-[#0A0D12] rounded-lg transition-colors">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 bg-[#0A0D12]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold mb-4 text-center">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-400 text-center mb-12">
            Common questions about Osrey and Osreys answered
          </p>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-[#1C2A35] rounded-lg border border-[#3A4E5F] overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-[#1C2A35]/80 transition-colors"
                >
                  <span className="text-lg font-semibold pr-8">
                    {faq.question}
                  </span>
                  <svg
                    className={`w-6 h-6 text-[#00D4AA] transition-transform flex-shrink-0 ${
                      openFaq === index ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5 text-gray-300 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-[#1C2A35] to-[#0A0D12]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">
            Can't Find What You're Looking For?
          </h2>
          <p className="text-gray-300 mb-8">
            Reach out to our team and we'll help you find the Osrey resources you need.
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-4 bg-[#00D4AA] hover:bg-[#00B890] text-[#0A0D12] font-bold rounded-lg transition-colors"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
}
